import { NavLink } from 'react-router-dom';
import {
  Bell,
  Briefcase,
  ClipboardList,
  CreditCard,
  Grid2X2,
  Heart,
  MessageSquare,
  Phone,
  Plus,
  Settings,
  X,
} from 'lucide-react';
import logoApp from '../../assets/images/logo.png';
import indoteknisiMan from '../../assets/images/indoteknisiMan-removebg.png';
import { useOwnerManpro } from '../context/OwnerManproContext';

type OwnerSidebarProps = {
  onClose: () => void;
};

type MenuItem = {
  path?: string;
  name: string;
  icon: typeof Grid2X2;
  badge?: string;
  exact?: boolean;
};

const menuItems: MenuItem[] = [
  { path: '/owner', name: 'Dashboard', icon: Grid2X2, exact: true },
  { path: '/owner/proyek-saya', name: 'Proyek Saya', icon: Briefcase },
  { path: '/owner/penawaran-masuk', name: 'Penawaran Masuk', icon: ClipboardList },
  { path: '/owner/favorit', name: 'Favorit', icon: Heart },
  { path: '/owner/vendor-tersimpan', name: 'Vendor Tersimpan', icon: Bell },
];

const navItemClassName = (isActive: boolean) =>
  [
    'flex items-center gap-3 rounded-xl px-3.5 py-3 text-sm font-semibold transition-all duration-200',
    isActive
      ? 'bg-blue-50 text-blue-700'
      : 'text-slate-700 hover:bg-blue-50/60 hover:text-blue-700',
  ].join(' ');

const iconClassName = (isActive: boolean) =>
  isActive ? 'text-blue-600' : 'text-slate-500 group-hover:text-blue-600';

const OwnerSidebar = ({ onClose }: OwnerSidebarProps) => {
  const { projectSummary } = useOwnerManpro();
  const incomingBidBadge = projectSummary.totalSubmittedBids > 0
    ? String(projectSummary.totalSubmittedBids)
    : undefined;

  return (
    <div className="flex h-[125vh] min-h-[125vh] w-[280px] flex-col border-r border-slate-200 bg-white">
      {/* Header */}
      <div className="flex items-start justify-between px-5 pb-6 pt-6">
        <div className="flex items-center gap-3">
          <img src={logoApp} alt="Indoteknisi" className="h-12 w-12 object-contain" />
          <div>
            <p className="text-2xl font-extrabold leading-none text-blue-700">Indoteknisi</p>
            <p className="mt-1 text-xs font-medium text-slate-500">Solusi Teknik & Konstruksi</p>
          </div>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="rounded-xl p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 lg:hidden"
          aria-label="Tutup sidebar"
        >
          <X size={18} />
        </button>
      </div>

      {/* Pasang Proyek Button */}
      
      <div className="px-5">
        <NavLink
          to="/owner/pasang"
          onClick={onClose}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-3 text-sm font-bold text-white shadow-lg shadow-blue-100 transition hover:bg-blue-700"
        >
          <Plus size={18} />
          Pasang Proyek Baru
        </NavLink>
      </div>

      {/* Main Menu SideBar */}
      <nav className="mt-6 flex-1 px-4 pb-4">
        <div className="space-y-1.5">
          {menuItems.map((item, index) => {
            const Icon = item.icon;
            // Badge penawaran memakai context MANPRO agar sidebar mengikuti data API, bukan angka statis prototype.
            const badge = item.name === 'Penawaran Masuk' ? incomingBidBadge : item.badge;

            if (!item.path) {
              return (
                <button
                  key={index}
                  type="button"
                  className="group flex w-full items-center gap-3 rounded-xl px-3.5 py-3 text-left text-sm font-semibold text-slate-700 transition hover:bg-blue-50/60 hover:text-blue-700"
                >
                  <Icon size={19} className="text-slate-500 group-hover:text-blue-600" />
                  <span className="flex-1">{item.name}</span>
                  {badge && (
                    <span className="rounded-full bg-red-500 px-2 py-0.5 text-xs font-extrabold text-white">
                      {badge}
                    </span>
                  )}
                </button>
              );
            }

            return (
              <NavLink key={item.name} to={item.path} end={item.exact} onClick={onClose}>
                {({ isActive }) => (
                  <div className={`group ${navItemClassName(isActive)}`}>
                    <Icon size={19} className={iconClassName(isActive)} />
                    <span className="flex-1">{item.name}</span>
                    {badge && (
                      <span className="rounded-full bg-red-500 px-2 py-0.5 text-xs font-extrabold text-white">
                        {badge}
                      </span>
                    )}
                  </div>
                )}
              </NavLink>
            );
          })}
        </div>
      </nav>

      {/* Help Section - Now with flex-shrink-0 and proper spacing */}
      <div className="flex-shrink-0 px-5 pb-6 pt-12">
        <div className="relative min-h-[190px] overflow-hidden rounded-2xl bg-slate-50 p-4 ring-1 ring-slate-200">
          <div className="relative z-10 max-w-[142px]">
            <h4 className="text-sm font-extrabold text-slate-800">Butuh Bantuan?</h4>
            <p className="mt-2 text-xs leading-5 text-slate-500">
              Tim Indoteknisi siap membantu Anda menemukan proyek terbaik.
            </p>
            <a
              href="https://wa.me/628123456789"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex rounded-lg bg-white px-3 py-2 text-xs font-extrabold text-blue-700 ring-1 ring-slate-200 transition hover:bg-blue-50"
            >
              Hubungi Kami
            </a>
            <a
              href="tel:02150992929"
              className="mt-3 flex items-center gap-2 text-xs font-bold text-slate-700 transition hover:text-blue-700"
            >
              <Phone size={15} className="text-blue-600" />
              (021) 5099 2929
            </a>
          </div>
          <img
            src={indoteknisiMan}
            alt="Tim support Indoteknisi"
            className="absolute -bottom-2 -right-5 h-[150px] w-auto object-contain drop-shadow-sm"
          />
        </div>
      </div>
    </div>
  );
};

export default OwnerSidebar;
